/**
 * Project recap — assembled at closeout from the task timeline.
 *
 * Delivered three ways:
 *   - JSON via GET /projects/:id/recap (used by the frontend preview)
 *   - PDF stream via GET /projects/:id/recap.pdf (downloadable / emailable)
 *   - Surfaced on the public status page once status=complete
 *
 * The "auto-assemble from timeline" pattern means Leo never writes a
 * recap — he just clicks Close project and reviews what the server built.
 */
import { z } from 'zod';
import { IsoDateTimeSchema, MoneyCentsSchema, UuidSchema } from './common.js';
import { AddressSchema } from './locations.js';
import { ScheduleDaySchema } from './projects.js';
import { RecapNoteSchema, RecapNoteAttachmentSchema } from './recap-notes.js';
import { PunchItemStatusSchema, TaskStatusSchema, TaskTypeSchema, ProjectStatusSchema, } from './enums.js';
/**
 * A warehouse pickup (its own entity, workstream C) that serviced this project.
 * Requester-appropriate: the run, when, and who — no internal routing detail.
 */
export const RecapPickupSchema = z.object({
    pickup_id: UuidSchema,
    title: z.string(),
    status: z.string(),
    scheduled_start: IsoDateTimeSchema.nullable(),
    finished_at: IsoDateTimeSchema.nullable(),
    crew: z.array(z.object({ display_name: z.string() })),
});
export const RecapCrewMemberSchema = z.object({
    display_name: z.string(),
    crew_role: z.string(),
    is_lead: z.boolean(),
});
export const RecapTaskSchema = z.object({
    id: UuidSchema,
    type: TaskTypeSchema,
    subtype: z.string().nullable(),
    title: z.string(),
    status: TaskStatusSchema,
    scheduled_start: IsoDateTimeSchema.nullable(),
    scheduled_end: IsoDateTimeSchema.nullable(),
    started_at: IsoDateTimeSchema.nullable(),
    completed_at: IsoDateTimeSchema.nullable(),
    actual_duration_minutes: z.number().int().min(0).nullable(),
    crew: z.array(RecapCrewMemberSchema),
    notes: z.string().nullable(),
    // For RMA pickup tasks
    sap_rma_reference: z.string().nullable(),
});
export const RecapStatusEventSchema = z.object({
    task_title: z.string(),
    from_status: TaskStatusSchema.nullable(),
    to_status: TaskStatusSchema,
    occurred_at: IsoDateTimeSchema,
    note: z.string().nullable(),
});
export const ProjectRecapResponseSchema = z.object({
    // Identity
    project_id: UuidSchema,
    title: z.string(),
    client_name: z.string(),
    status: ProjectStatusSchema,
    status_page_slug: z.string(),
    // SAP / design linkage
    sap_order_number: z.string().nullable(),
    sap_project_id: z.string().nullable(),
    customer_number: z.string().nullable(),
    design_job_number: z.string().nullable(),
    design_pd_number: z.string().nullable(),
    // Team
    pm_display_name: z.string().nullable(),
    interior_designer: z.string().nullable(),
    csm: z.string().nullable(),
    sales_executive: z.string().nullable(),
    // Site
    site_address: AddressSchema.nullable(),
    site_contact_name: z.string().nullable(),
    // Schedule
    requested_start_date: IsoDateTimeSchema.nullable(),
    requested_end_date: IsoDateTimeSchema.nullable(),
    /** Committed per-day working hours (per-day schedule feature). */
    approved_schedule: z.array(ScheduleDaySchema).nullable(),
    completed_at: IsoDateTimeSchema.nullable(),
    // Money
    delivery_install_budget_cents: MoneyCentsSchema.nullable(),
    actual_cost_cents: MoneyCentsSchema.nullable(),
    // Body
    tasks: z.array(RecapTaskSchema),
    status_events: z.array(RecapStatusEventSchema),
    // Rolled-up crew across all tasks (deduped)
    crew_roster: z.array(RecapCrewMemberSchema),
    // Aggregates
    /** Sum of per-task durations + direct project work (clock time), independent of crew size. */
    total_duration_minutes: z.number().int().min(0),
    /** Actual man-hours worked = Σ (task duration × crew) + direct project work × its crew. */
    total_crewed_minutes: z.number().int().min(0),
    /**
     * Direct project work (D-3/A3/A5): clock minutes the crew worked the PROJECT
     * itself (Start→Finish), with no task — so the totals reconcile with the task
     * rows for a project worked directly. 0 when all work went through tasks.
     * Already included in total_duration_minutes / total_crewed_minutes.
     */
    project_worked_minutes: z.number().int().min(0).default(0),
    /** The crew who held project-anchored blocks — who the direct work belongs to. */
    project_crew: z.array(RecapCrewMemberSchema).default([]),
    /**
     * Warehouse pickups (workstream C) that serviced this project — the runs from
     * `pickup_projects`, with their crew. Listed for the record; their labor is
     * NOT folded into this project's totals (one run can span several projects,
     * and splitting or double-counting it per project would both mislead).
     */
    pickups: z.array(RecapPickupSchema).default([]),
    /**
     * The supervisor's *committed* man-hours — the floor the requester is told
     * up front (demo #9). Sourced from the bid's total man-hours
     * (`bid_labor_hours`); null when there's no bid to commit a number. When
     * present this — not the derived actual — is the headline the recap leads
     * with; actual only surfaces as an overage.
     */
    committed_man_minutes: z.number().int().min(0).nullable(),
    /**
     * Man-hours actual ran *over* the committed floor (0 when at/under, or when
     * there's no committed floor). Drives the "ran X over committed" callout.
     */
    overage_man_minutes: z.number().int().min(0),
    rma_count: z.number().int().min(0),
    punch_items_total: z.number().int().min(0),
    punch_items_closed: z.number().int().min(0),
    /** Open punch items flagged "blocks closeout" — the close endpoint refuses
     *  while any remain (audit 2026-08-27). */
    punch_items_blocking_open: z.number().int().min(0).default(0),
    /**
     * The still-open punch items themselves, so the closeout screen can resolve
     * them IN PLACE (close, or clear the blocking flag) instead of sending the
     * supervisor off to each task's punch list (Ben 2026-08-27).
     */
    open_punch_items: z
        .array(z.object({
        id: UuidSchema,
        title: z.string(),
        /** Current lifecycle state — an undo after a closeout-screen close restores this. */
        status: PunchItemStatusSchema,
        blocks_recap: z.boolean(),
        task_id: UuidSchema,
        task_title: z.string(),
    }))
        .default([]),
    // Notes & URLs
    intake_notes: z.string().nullable(),
    internal_notes: z.string().nullable(),
    /**
     * Crew-captured recap notes (photos + context). INTERNAL source material for
     * the supervisor's closeout curation — never rendered to the requester. The
     * requester sees `recap_narrative` (the single blurb) + `recap_gallery`.
     */
    recap_notes: z.array(RecapNoteSchema).default([]),
    /**
     * The supervisor's curated, ordered photo gallery — the images they opted into
     * the recap at closeout (a subset of the crew's captured photos). This, not the
     * per-note photos, is what ships in the PDF / web recap / email.
     */
    recap_gallery: z.array(RecapNoteAttachmentSchema).default([]),
    /** Closeout narrative the supervisor reviews/edits before sending (F.4). */
    recap_narrative: z.string().nullable(),
    recap_url: z.string().url().nullable(),
    recap_pdf_url: z.string().url().nullable(),
});
/** Body for POST /projects/:id/close — optionally persists the final narrative. */
export const ProjectCloseSchema = z.object({
    recap_narrative: z.string().max(10_000).optional().nullable(),
});
/**
 * Body for PUT /projects/:id/recap/gallery — the supervisor's curated, ordered
 * list of image attachment ids for the closeout recap. Order is preserved.
 */
export const RecapGalleryUpdateSchema = z.object({
    image_ids: z.array(UuidSchema).max(60),
});
/** Response for the gallery-update endpoint. */
export const RecapGalleryResponseSchema = z.object({
    image_ids: z.array(UuidSchema),
});
//# sourceMappingURL=recap.js.map